import { KeyValue } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { forkJoin, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CatalogFilter } from 'src/app/services/catalog.service';
import { ContractService } from 'src/app/services/contract.service';
import { ProjectService } from 'src/app/services/project.service';
import { RegionAreaService } from 'src/app/services/region-area.service';
import { ServiceOrderService } from 'src/app/services/service-order.service';
import { TeamService } from 'src/app/services/team.service';
import { VehicleService } from 'src/app/services/vehicles.service';
import { enumerators } from 'src/dictionaries/enum.pt-br';
import { DeviceSignalingEnum, HorizontalSignalingEnum, OccurrenceStatusEnum, PriorityEnum, TaskStatusEnum, VerticalGroupTypeEnum } from 'src/enumerators';
import { FilterOptionsEnum } from 'src/enumerators/filter-options.enum';
import { VerticalSignalingEnum } from 'src/enumerators/vertical-signaling.enum';
import {
  Area,
  Contract,
  ContractDeviceItem,
  ContractHorizontalItem,
  ContractVerticalItemSign,
  ContractVerticalItemSupport,
  Member,
  Project,
  Region,
  ServiceOrder,
  Team,
  Vehicle
} from 'src/models';

export interface FilterOption {
  enumText: string;
  type: 'ADDRESS' | 'DATE' | 'TEXT' | 'VALUE';
  check?: boolean;
  optionList?: any[];
  selected?: any[];
  timeInterval?: any;
  title?: any;
  attribute: keyof CatalogFilter | '';
}

type VerticalContractFilterItem = (ContractVerticalItemSign | ContractVerticalItemSupport) & {
  contractId?: string;
  groupType?: VerticalGroupTypeEnum;
};

type HorizontalContractFilterItem = ContractHorizontalItem & {
  contractId?: string;
  groupType?: 'HORIZONTAL';
};

type DeviceContractFilterItem = ContractDeviceItem & {
  contractId?: string;
  groupType?: 'DEVICE';
};

@Component({
  selector: 'app-filter-modal',
  templateUrl: './filter-modal.component.html',
  styleUrls: ['./filter-modal.component.css']
})
export class FilterModalComponent implements OnInit {

  @Input() filterOptions: FilterOptionsEnum[] = [];
  @Input() removalFilter: boolean = false; // Apenas para sinalizações
  @Input() activeFilters: { option: FilterOptionsEnum, selected: any }[] = [];

  @Output() emitFilters = new EventEmitter<{ catalogFilter: CatalogFilter, activeFilters: { option: FilterOptionsEnum, selected: any }[] }>;
  @Output() emitCloseModal = new EventEmitter<boolean>;

  modalsRef: BsModalRef[] = [];

  loading = true;

  filterOptionsAux: Map<FilterOptionsEnum, boolean> = new Map();
  filterTemp: Map<FilterOptionsEnum, any[]> = new Map();
  checkOptionBool = false; // Variável auxiliar para saber se tem algum filtro selecionado ou não
  checkRemovalFilter = false;

  // Lista de opções dos filtros
  @Input() contractList: Contract[] = [];
  @Input() projectList: Project[] = [];
  @Input() regionList: Region[] = [];
  @Input() areaList: Area[] = [];
  @Input() memberList: Member[] = [];
  @Input() contractVerticalItemList: VerticalContractFilterItem[] = [];
  @Input() contractHorizontalItemList: HorizontalContractFilterItem[] = [];
  @Input() contractDeviceItemList: DeviceContractFilterItem[] = [];
  @Input() teamList: Member[] = [];
  @Input() vehicleList: Vehicle[] = [];
  @Input() serviceOrderList: ServiceOrder[] = [];

  verticalList = Object.values(VerticalSignalingEnum).map(p => { return { name: enumerators.Signaling[p], id: p } })
  horizontalList = Object.values(HorizontalSignalingEnum).map(p => { return { name: enumerators.Signaling[p], id: p } })
  deviceList = Object.values(DeviceSignalingEnum).map(p => { return { name: enumerators.Signaling[p], id: p } })
  occurrencesStatusList = Object.values(OccurrenceStatusEnum).map(p => { return { name: enumerators.OccurrenceStatus[p], id: p } })
  taskPriorityList = Object.values(PriorityEnum).map(p => { return { name: enumerators.TaskPriority[p], id: p } })
  taskStatusList = Object.values(TaskStatusEnum).map(p => { return { name: enumerators.TaskStatus[p], id: p } })

  // Caso tenha 'title' e 'view' é pra 'juntar' na hora de escolher os filtros e separar quando escolher
  completeFilterOptions: Map<FilterOptionsEnum, FilterOption> = new Map([
    [FilterOptionsEnum.Contract, { attribute: 'contractIds', enumText: '', type: 'VALUE', check: false, optionList: this.contractList, selected: [] }],
    [FilterOptionsEnum.Project, { attribute: 'projectIds', enumText: '', type: 'VALUE', check: false, optionList: this.projectList, selected: [] }],
    [FilterOptionsEnum.VerticalSignaling, { attribute: 'verticalCodes', enumText: 'Signaling', type: 'VALUE', check: false, optionList: this.verticalList, selected: [], title: 'Sinalizações', view: true }],
    [FilterOptionsEnum.HorizontalSignaling, { attribute: 'horizontalCodes', enumText: 'Signaling', type: 'VALUE', check: false, optionList: this.horizontalList, selected: [], title: 'Sinalizações', view: false }],
    [FilterOptionsEnum.DeviceSignaling, { attribute: 'deviceCodes', enumText: 'Signaling', type: 'VALUE', check: false, optionList: this.deviceList, selected: [], title: 'Sinalizações', view: false }],
    [FilterOptionsEnum.Region, { attribute: 'regionIds', enumText: '', type: 'VALUE', check: false, optionList: this.regionList, selected: [] }],
    [FilterOptionsEnum.Area, { attribute: 'areaIds', enumText: '', type: 'VALUE', check: false, optionList: this.areaList, selected: [] }],
    [FilterOptionsEnum.TimeInterval, { attribute: '', enumText: '', type: 'DATE', check: false, optionList: [], selected: [], timeInterval: undefined }],
    [FilterOptionsEnum.Responsible, { attribute: 'responsibleMemberIds', enumText: '', type: 'VALUE', check: false, optionList: this.memberList, selected: [] }],
    [FilterOptionsEnum.Address, { attribute: 'address', enumText: '', type: 'ADDRESS', check: false, optionList: [], selected: [] }],
    [FilterOptionsEnum.MaintenanceTimeInterval, { attribute: '', enumText: '', type: 'DATE', check: false, optionList: [], selected: [], timeInterval: undefined }],
    [FilterOptionsEnum.ContractVerticalItem, { attribute: 'contractVerticalItemIds', enumText: '', type: 'VALUE', check: false, optionList: this.contractVerticalItemList, selected: [], title: 'Itens de Contrato', view: true }],
    [FilterOptionsEnum.ContractHorizontalItem, { attribute: 'contractHorizontalItemIds', enumText: '', type: 'VALUE', check: false, optionList: this.contractHorizontalItemList, selected: [], title: 'Itens de Contrato', view: false }],
    [FilterOptionsEnum.ContractDeviceItem, { attribute: 'contractDeviceItemIds', enumText: '', type: 'VALUE', check: false, optionList: this.contractDeviceItemList, selected: [], title: 'Itens de Contrato', view: false }],
    [FilterOptionsEnum.Occurrences, { attribute: 'occurrenceStatus', enumText: '', type: 'VALUE', check: false, optionList: this.occurrencesStatusList, selected: [] }],
    [FilterOptionsEnum.TeamLeader, { attribute: 'leaderMemberIds', enumText: '', type: 'VALUE', check: false, optionList: this.teamList, selected: [] }],
    [FilterOptionsEnum.TaskStatus, { attribute: 'taskStatus', enumText: '', type: 'VALUE', check: false, optionList: this.taskStatusList, selected: [] }],
    [FilterOptionsEnum.TaskPriority, { attribute: 'taskPriority', enumText: '', type: 'VALUE', check: false, optionList: this.taskPriorityList, selected: [] }],
    [FilterOptionsEnum.Vehicle, { attribute: 'vehicleIds', enumText: '', type: 'VALUE', check: false, optionList: this.vehicleList, selected: [] }],
    [FilterOptionsEnum.ServiceOrder, { attribute: 'serviceOrderIds', enumText: '', type: 'VALUE', check: false, optionList: this.serviceOrderList, selected: [] }],
    [FilterOptionsEnum.RegisterId, { attribute: 'ids', enumText: '', type: 'TEXT', check: false, optionList: [], selected: [] }],
  ]);

  usedFilterOptions: Map<FilterOptionsEnum, FilterOption> = new Map();

  constructor(
    private _teamService: TeamService,
    private _modalService: BsModalService,
    private _vehicleService: VehicleService,
    private _projectService: ProjectService,
    private _contractService: ContractService,
    private _regionAreaService: RegionAreaService,
    private _serviceOrderService: ServiceOrderService,

  ) { }

  ngOnInit(): void {
    this.usedFilterOptions = new Map(
      [...this.completeFilterOptions].filter(([key]) => this.filterOptions.includes(key))
    );
    this.syncOptionLists();

    this.activeFilters.forEach(filter => {
      const selectedOption = this.usedFilterOptions.get(filter.option);
      if (!selectedOption) {
        return;
      }

      selectedOption.check = true;
      selectedOption.selected =
        selectedOption.type === 'DATE'
          ? this.normalizeSelectedDateRange(filter.selected)
          : this.cloneSelectedValue(filter.selected);
    });
    if (this.activeFilters.some(filter => this.usedFilterOptions.has(filter.option))) {
      this.checkOptionBool = true;
    }
    this.saveTemp()

    const requests: Observable<unknown>[] = [];

    if (!this.contractList.length) requests.push(this.getContractFull());
    if (!this.projectList.length) requests.push(this.getProject());
    if (!this.areaList.length) requests.push(this.getArea());
    if (!this.regionList.length) requests.push(this.getRegions());
    if (!this.memberList.length) requests.push(this.getUsers());
    if (!this.teamList.length) requests.push(this.getTeams());
    if (!this.vehicleList.length) requests.push(this.getVehicles());
    if (this.filterOptions.includes(FilterOptionsEnum.ServiceOrder) && !this.serviceOrderList.length) {
      requests.push(this.getServiceOrders());
    }

    if (requests.length > 0) {
      // Usa o forkJoin para aguardar todas as requisições
      forkJoin(requests).subscribe(() => {
        // Aqui todas as requisições foram concluídas, finaliza o loading
        this.memberList = this.memberList.sort((a, b) => {
          const nameA = a.user.firstName.toLowerCase();
          const nameB = b.user.firstName.toLowerCase();
          const lastNameA = a.user.lastName.toLowerCase();
          const lastNameB = b.user.lastName.toLowerCase();

          if (nameA === nameB) {
            return lastNameA.localeCompare(lastNameB);
          }
          return nameA.localeCompare(nameB);
        });
        this.syncOptionLists();
        this.loading = false;
      });
    }
    else {
      this.syncOptionLists();
      this.loading = false;
    }

  }

  private syncOptionLists() {
    const optionListsByFilter = new Map<FilterOptionsEnum, any[]>([
      [FilterOptionsEnum.Contract, this.contractList],
      [FilterOptionsEnum.Project, this.projectList],
      [FilterOptionsEnum.Region, this.regionList],
      [FilterOptionsEnum.Area, this.areaList],
      [FilterOptionsEnum.Responsible, this.memberList],
      [FilterOptionsEnum.ContractVerticalItem, this.contractVerticalItemList],
      [FilterOptionsEnum.ContractHorizontalItem, this.contractHorizontalItemList],
      [FilterOptionsEnum.ContractDeviceItem, this.contractDeviceItemList],
      [FilterOptionsEnum.TeamLeader, this.teamList],
      [FilterOptionsEnum.Vehicle, this.vehicleList],
      [FilterOptionsEnum.ServiceOrder, this.serviceOrderList],
    ]);

    optionListsByFilter.forEach((optionList, filterOption) => {
      const option = this.usedFilterOptions.get(filterOption);
      if (option) {
        const uniqueOptionList = this.dedupeOptionList(optionList ?? []);
        option.optionList = optionList ? this.replaceListContents(optionList, uniqueOptionList) : uniqueOptionList;
      }
    });
  }

  getContractFull(): Observable<Contract[]> {
    return this._contractService.listFullContracts().pipe(
      tap((res: Contract[]) => {
        const contractList: Contract[] = [];
        const verticalItems: VerticalContractFilterItem[] = [];
        const horizontalItems: HorizontalContractFilterItem[] = [];
        const deviceItems: DeviceContractFilterItem[] = [];

        res.forEach((contract) => {
          contractList.push(contract);
          contract.verticalGroups.forEach(group => {
            group.verticalItems.forEach(item => {
              const verticalItem = item as VerticalContractFilterItem;
              verticalItem.groupType = group.groupType;
              verticalItem.contractId = contract.id;
              verticalItems.push(verticalItem);
            });
          });
          contract.horizontalGroups.forEach(group => {
            group.horizontalItems.forEach(item => {
              const horizontalItem = item as HorizontalContractFilterItem;
              horizontalItem.contractId = contract.id;
              horizontalItem.groupType = 'HORIZONTAL';
              horizontalItems.push(horizontalItem);
            });
          });
          contract.deviceGroups.forEach(group => {
            group.deviceItems.forEach(item => {
              const deviceItem = item as DeviceContractFilterItem;
              deviceItem.contractId = contract.id;
              deviceItem.groupType = 'DEVICE';
              deviceItems.push(deviceItem);
            });
          });
        });

        this.contractList = this.replaceListContents(
          this.contractList,
          this.sortByName(this.dedupeOptionList(contractList)),
        );
        this.contractVerticalItemList = this.replaceListContents(
          this.contractVerticalItemList,
          this.dedupeOptionList(verticalItems),
        );
        this.contractHorizontalItemList = this.replaceListContents(
          this.contractHorizontalItemList,
          this.dedupeOptionList(horizontalItems),
        );
        this.contractDeviceItemList = this.replaceListContents(
          this.contractDeviceItemList,
          this.dedupeOptionList(deviceItems),
        );
      })
    );
  }

  getProject(): Observable<Project[]> {
    return this._projectService.getProjects().pipe(
      map((res) => this.toArray<Project>(res)),
      tap((res: Project[]) => {
        this.projectList = this.replaceListContents(
          this.projectList,
          this.sortByName(this.dedupeOptionList(res)),
        );
      })
    );
  }

  getRegions(): Observable<Region[]> {
    return this._regionAreaService.getRegions().pipe(
      map((res) => this.toArray<Region>(res)),
      tap((res: Region[]) => {
        this.regionList = this.replaceListContents(
          this.regionList,
          this.sortByName(this.dedupeOptionList(res)),
        );
      })
    );
  }

  getArea(): Observable<Area[]> {
    return this._regionAreaService.getAreas().pipe(
      map((res) => this.toArray<Area>(res)),
      tap((res: Area[]) => {
        this.areaList = this.replaceListContents(
          this.areaList,
          this.sortByName(this.dedupeOptionList(res)),
        );
      })
    );
  }

  getUsers(): Observable<any> {
    return this._contractService.listAllWorkspaceUsers({ registeredSignaling: true, files: false }).pipe(
      tap((res: any) => {
        this.memberList = this.replaceListContents(
          this.memberList,
          this.dedupeOptionList(res.members ?? []),
        );
        if (!this.teamList.length) this.getTeams();  // Aqui não precisa retornar Observable, pois já será tratado fora
      })
    );
  }

  getTeams(): Observable<Team[]> {
    return this._teamService.getTeams().pipe(
      map((res) => this.toArray<Team>(res)),
      tap((res: Team[]) => {
        res.forEach((team: Team) => {
          team.leaderMember = this.memberList.find(user => user.id === team.leaderMemberId);
        });
        this.teamList = this.replaceListContents(
          this.teamList,
          this.dedupeOptionList(
            res
              .map((team: Team) => team.leaderMember)
              .filter((leaderMember: Member | undefined): leaderMember is Member => !!leaderMember),
          ),
        );
      })
    );
  }

  getVehicles(): Observable<Vehicle[]> {
    return this._vehicleService.getVehicles().pipe(
      map((res) => this.toArray<Vehicle>(res)),
      tap((vehicles: Vehicle[]) => {
        this.vehicleList = this.replaceListContents(
          this.vehicleList,
          this.dedupeOptionList(vehicles),
        );
      })
    );
  }

  getServiceOrders(): Observable<ServiceOrder[]> {
    return this._serviceOrderService.getOrders().pipe(
      map((res) => this.toArray<ServiceOrder>(res)),
      tap((orders: ServiceOrder[]) => {
        this.serviceOrderList = this.replaceListContents(
          this.serviceOrderList,
          this.sortByName(this.dedupeOptionList(orders ?? [])),
        );
      })
    );
  }

  closeModalEmit() {
    this.emitCloseModal.emit(true)
  }

  // Abre MODAL
  openModal(template: TemplateRef<any>) {
    const modalRefAux: BsModalRef = this._modalService.show(template, {
      class: 'modal-dialog-centered',
      backdrop: 'static',
    });
    this.modalsRef.push(modalRefAux);
  }

  // Fecha o último modal aberto
  closeModal() {
    const last = this.modalsRef.length - 1;
    if (last < 0) {
      return;
    }
    const modalRef = this.modalsRef[last];
    if (!modalRef) {
      return;
    }
    modalRef.hide();
    this.modalsRef.pop();
  }

  private toArray<T>(value: T | T[] | unknown): T[] {
    if (Array.isArray(value)) {
      return value;
    }

    return value ? [value as T] : [];
  }

  private hasCatalogAttribute(attribute: FilterOption['attribute']): attribute is keyof CatalogFilter {
    return attribute !== '';
  }

  private setCatalogFilterValue<K extends keyof CatalogFilter>(
    catalogFilter: CatalogFilter,
    attribute: K,
    value: CatalogFilter[K]
  ) {
    catalogFilter[attribute] = value;
  }

  private replaceListContents<T>(target: T[], values: T[]): T[] {
    target.splice(0, target.length, ...values);
    return target;
  }

  private getOptionItemKey(item: any): string | undefined {
    const label = this.getOptionItemLabel(item);
    if (label) {
      return `label:${label}`;
    }

    if (item?.id != undefined && item?.id !== null) {
      return `id:${String(item.id)}`;
    }

    if (item?.user?.id != undefined && item?.user?.id !== null) {
      return `user:${String(item.user.id)}`;
    }

    return undefined;
  }

  private getOptionItemLabel(item: any): string | undefined {
    const label =
      item?.name ??
      [item?.user?.firstName, item?.user?.lastName].filter(Boolean).join(' ').trim();

    return label ? String(label).trim().toLocaleLowerCase() : undefined;
  }

  private dedupeOptionList<T>(list: T[] = []): T[] {
    const optionMap = new Map<string, T>();

    list.forEach((item) => {
      const key = this.getOptionItemKey(item);
      if (!key || optionMap.has(key)) {
        return;
      }

      optionMap.set(key, item);
    });

    return Array.from(optionMap.values());
  }

  private cloneSelectedValue(value: any): any {
    if (value instanceof Date) {
      return new Date(value.getTime());
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.cloneSelectedValue(item));
    }

    if (value && typeof value === 'object') {
      return { ...value };
    }

    return value;
  }

  private normalizeSelectedDateRange(selected: any[] = []): Date[] {
    const rawRange =
      Array.isArray(selected?.[0]) && selected[0].length
        ? selected[0]
        : selected;

    return rawRange
      .slice(0, 2)
      .map((value) => this.toDate(value))
      .filter((value): value is Date => !!value);
  }

  private toDate(value: any): Date | undefined {
    if (!value) {
      return undefined;
    }

    if (value instanceof Date) {
      return new Date(value.getTime());
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
  }

  // Comparador que preserva a ordem de inserção no Map
  originalOrder = (a: KeyValue<string, FilterOption>, b: KeyValue<string, FilterOption>): number => {
    return 0;  // Retorna 0 para não alterar a ordem
  };

  // Salva o check filterOptionCheck após ser alterado todos os checks no modal
  saveCheckFilter() {
    let sum = 0;
    this.usedFilterOptions.forEach((option, key) => {
      const auxOption = this.filterOptionsAux.get(key) ?? false;
      option.check = auxOption;
      if (auxOption) sum += 1;
      else option.selected = []; // Caso vire falso, reinicia as opções selecionadas
    });
    this.checkOptionBool = sum > 0;

  }

  // Salva variável temporária ao abrir o modal
  saveTemp() {
    this.filterTemp.clear();
    this.filterOptionsAux.clear();

    this.usedFilterOptions.forEach((option, key) => {
      this.filterTemp.set(key, this.cloneSelectedValue(option.selected ?? []));
      this.filterOptionsAux.set(key, option.check ?? false);

    });
  }

  // Cancela o filtro, retorna como era antes de abrir o filtro
  cancelFilter() {
    this.usedFilterOptions.forEach((option, key) => {
      const tempSelected = this.filterTemp.get(key);
      if (tempSelected) {
        option.selected = this.cloneSelectedValue(tempSelected); // Restaura o valor anterior
      }
    });
  }

  changeCheckFilter(keyOption: FilterOptionsEnum) {
    let value = !this.filterOptionsAux.get(keyOption);
    this.filterOptionsAux.set(keyOption, value);
    // Pros casos de sinalizações e itens de contrato, abrir os filtros derivados
    if (this.usedFilterOptions.get(keyOption)?.title) {
      if (keyOption == FilterOptionsEnum.VerticalSignaling) {
        this.filterOptionsAux.set(FilterOptionsEnum.HorizontalSignaling, value)
        this.filterOptionsAux.set(FilterOptionsEnum.DeviceSignaling, value)

      } else if (keyOption == FilterOptionsEnum.ContractVerticalItem) {
        this.filterOptionsAux.set(FilterOptionsEnum.ContractHorizontalItem, value)
        this.filterOptionsAux.set(FilterOptionsEnum.ContractDeviceItem, value)
      }
    }

  }

  sendFilter() {
    const catalogFilter = this.convertToCatalogFilter();
    const activeFilters: { option: FilterOptionsEnum; selected: any }[] = [...this.usedFilterOptions.entries()]
      .filter(([_, value]) => value.check) // Filtra os que têm check: true
      .map(([key, value]) => ({
        option: key, // A chave do Map
        selected: value.selected // O campo selected dentro do value
      }));
    this.emitFilters.emit({ catalogFilter, activeFilters });
    this.closeModalEmit();
  }

  receiveSelected(event: any[], key: FilterOptionsEnum) {
    const option = this.usedFilterOptions.get(key);
    if (!option) {
      return;
    }

    option.selected = event;
  }

  convertToCatalogFilter(): CatalogFilter {
    const catalogFilter: CatalogFilter = {};

    this.usedFilterOptions.forEach((option, key) => {
      if (option.selected && option.selected.length > 0) {
        if (key == FilterOptionsEnum.TimeInterval) {
          const [startDate, endDate] = this.normalizeSelectedDateRange(option.selected);
          catalogFilter.startDate = startDate;
          catalogFilter.endDate = endDate;

        } else if (key == FilterOptionsEnum.MaintenanceTimeInterval) {
          const [startMaintenanceDate, endMaintenanceDate] = this.normalizeSelectedDateRange(option.selected);
          catalogFilter.startMaintenanceDate = startMaintenanceDate;
          catalogFilter.endMaintenanceDate = endMaintenanceDate;

        } else if (key == FilterOptionsEnum.Address) {
          const addressJson = JSON.stringify({
            road: option.selected[0]?.type === 'road' ? option.selected[0].name : undefined,
            suburb: option.selected[0]?.type === 'suburb' ? option.selected[0].name : undefined,
          });
          if (this.hasCatalogAttribute(option.attribute)) {
            this.setCatalogFilterValue(catalogFilter, option.attribute, addressJson);
          }

        } else if (this.hasCatalogAttribute(option.attribute)) {
          this.setCatalogFilterValue(catalogFilter, option.attribute, option.selected);
        }

      }
    });

    return catalogFilter;
  }

  sortByName<T extends { name: string }>(list: T[]): T[] {
    return list.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
  }
}
